import { useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { routeAccess } from '@/lib/api/route-access';
import { usePermissions } from '@/lib/permissions';

interface RequirePermissionProps {
  /** User must hold this permission to view the route. */
  permission?: string;
  /** ...or AT LEAST ONE of these. */
  anyOf?: string[];
  children: React.ReactNode;
  /** Where to send unauthorized users (defaults to the dashboard). */
  redirectTo?: string;
}

/**
 * Route guard that gates a whole route by permission. Mirrors `RequireRole`
 * but checks the current user's effective permissions. Redirects unauthorized
 * users to the dashboard (or `redirectTo`).
 */
export function RequirePermission({
  permission,
  anyOf,
  children,
  redirectTo = '/admin/dashboard',
}: RequirePermissionProps) {
  const { can, canAny } = usePermissions();

  const allowed =
    (permission ? can(permission) : true) && (anyOf ? canAny(anyOf) : true);

  useEffect(() => {
    if (!allowed) return;
    routeAccess.enter();
    return () => routeAccess.leave();
  }, [allowed]);

  if (!allowed) {
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
}
